import basketCircle from "assets/img/svg/basketCircle";
import { Pending } from "components/Management/ModalContent";
import ModalBox from "components/Modal";
import StyledButton from "components/shared/StyledButton";
import type { StyledButtonType } from "components/shared/StyledButton";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";

import MessageBlock from "../MessageBlock";
import StyledTextField from "../StyledTextField/StyledTextField";
import styles from "./style.module.scss";

interface DeleteModalProps {
    open: boolean;
    onClose: () => void;
    /**
     * Function to trigger when they confirm deletion.
     */
    onDelete: () => Promise<void>;
    /**
     * Name of the element being deleted.
     */
    elementTitle: string;
    /**
     * Each element of the array is one line of the description.
     */
    description?: ReactNode;
    archive?: boolean;
    /**
     * If true, we display the field for them to write the confirmation text.
     */
    needsConfirmation?: boolean;
    /**
     * Confirmation text to write within field.
     */
    confirmationText?: string;
    /**
     * If true, we place elementTitle between double quotes (ex: for titles)
     */
    needsDoubleQuotes?: boolean;
    /**
     * Message that appears in yellow warning block. Default text is: Cette action est irréversible.
     */
    warningMessage?: string;
    /**
     * Custom modal title. Replaces the default delete/archive question.
     */
    title?: ReactNode;
    /**
     * Custom confirm button label.
     */
    confirmLabel?: string;
    /**
     * Custom confirm button type. Defaults to danger.
     */
    confirmButtonType?: StyledButtonType;
    /**
     * Optional icon displayed in the confirm button.
     */
    confirmIcon?: ReactNode;
}

const DeleteModal = (props: DeleteModalProps) => {
    const {
        open,
        onClose,
        onDelete,
        elementTitle,
        description,
        archive = false,
        needsConfirmation = false,
        confirmationText,
        needsDoubleQuotes = false,
        warningMessage,
        title,
        confirmLabel,
        confirmButtonType = "danger",
        confirmIcon,
    } = props;
    const [t] = useTranslation();
    const { deleteTitleStatus } = useAppSelector((state) => state.titles);

    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleChangeInputValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleDelete = async () => {
        setIsLoading(true);
        setInputValue("");
        await onDelete();
        setIsLoading(false);
        onClose();
    };

    const disableDeleteBtn =
        isLoading || (needsConfirmation && inputValue?.toUpperCase() !== confirmationText?.toUpperCase());

    return (
        <>
            <ModalBox open={deleteTitleStatus === "pending"} onClose={() => {}} showCloseButton={false}>
                <Pending phrase="" />
            </ModalBox>
            <ModalBox open={open} onClose={() => onClose()} Icon={basketCircle}>
                <div className={styles["delete-modal-wrapper"]}>
                    <div className={styles["delete-title"]}>
                        {title ? (
                            <h3>{title}</h3>
                        ) : archive ? (
                            <h3>
                                {t("DeleteModal.DeleteQuestion")}
                                {needsDoubleQuotes
                                    ? t("DeleteModal.ArchiveQuotes", { element: elementTitle })
                                    : t("DeleteModal.Archive", { element: elementTitle })}
                            </h3>
                        ) : (
                            <h3>
                                {t("DeleteModal.DeleteQuestion")}
                                {needsDoubleQuotes
                                    ? t("DeleteModal.DeleteQuotes", { element: elementTitle })
                                    : t("DeleteModal.Delete", { element: elementTitle })}
                            </h3>
                        )}
                        <MessageBlock type="warning" text={warningMessage ?? t("DeleteModal.AlertMessage")} small />
                    </div>
                    {description && (
                        <div className={styles["delete-description"]}>
                            {typeof description === "string" ? <p>{description}</p> : description}
                        </div>
                    )}
                    {needsConfirmation && (
                        <div className={styles["confirmation-input"]}>
                            <span className={styles["input-label"]}>
                                {t("DeleteModal.Copy", { text: confirmationText?.toUpperCase() ?? "" })}
                            </span>
                            <StyledTextField
                                value={inputValue}
                                onChange={handleChangeInputValue}
                                fullWidth
                                placeholder={t("DeleteModal.EnterText")}
                            />
                        </div>
                    )}
                    <div className={styles["delete-buttons"]}>
                        <StyledButton onClick={() => onClose()} fullWidth buttonType="secondary">
                            {t("DeleteModal.Cancel")}
                        </StyledButton>
                        <StyledButton
                            onClick={handleDelete}
                            fullWidth
                            buttonType={confirmButtonType}
                            loading={isLoading}
                            disabled={disableDeleteBtn}
                        >
                            {!isLoading && (
                                <>
                                    {confirmIcon}
                                    {confirmLabel ??
                                        (archive ? t("DeleteModal.ConfirmArchive") : t("DeleteModal.ConfirmDelete"))}
                                </>
                            )}
                        </StyledButton>
                    </div>
                </div>
            </ModalBox>
        </>
    );
};

export default DeleteModal;
